import 'braid-design-system/reset';

import { ApolloProvider } from '@apollo/client';
import { brandAndCountryFromZone } from '@seek/audience-zones';
import { compatibility, device } from '@seek/ca-myprofile-utils';
import { MelwaysProvider } from '@seek/melways-react';
import { type Country, getZoneFromSite, type Brand } from '@seek/melways-sites';
import { setup as setupMetrics, metrics } from '@seek/metrics-js';
import { VocabProvider } from '@vocab/react';
import { BraidProvider, ToastProvider } from 'braid-design-system';
import seekJobs from 'braid-design-system/themes/seekJobs';
import promise from 'es6-promise';
promise.polyfill();
import 'raf/polyfill';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import type { Site } from 'src/config';
import apiClient from 'src/modules/api';
import { initBrowserLog } from 'src/modules/error-logger/browserLog';

import { App } from './components/App/App';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import createStore from './createStore';
import { ApiProvider } from './modules/ApiProvider';
import { createAnalytics } from './modules/analytics/analytics';
import { hydrateFeatures } from './modules/features';
import { updatePageBrandCountryZone } from './reducers/page/actionCreators';
import type { ClientContext } from './types';

export default ({ language: vocabLanguage, site }: ClientContext) => {
  const zone = getZoneFromSite(site as Site);
  const [brand, upperCountry] = brandAndCountryFromZone(zone) || ['seek', 'AU'];
  const country = upperCountry.toLowerCase() as Country;
  const languageCode =
    compatibility.getCompatibleLanguageFromLocaleString(vocabLanguage);

  setupMetrics({
    host: window.CONFIG.METRICS_URL,
    isEnabled: window.CONFIG.METRICS_ENABLED,
    prefix: 'ca_settings_ui',
    globalTags: [
      `environment:${window.CONFIG.ENV}`,
      `browser-name:${device.getBrowserName()}`,
      `browser-version:${device.getBrowserVersion()}`,
      `device-type:${device.getDeviceType()}`,
      `app-version:${window.CONFIG.VERSION}`,
      `country:${country}`,
      `language:${languageCode}`,
      `zone:${zone}`,
    ],
  });

  metrics.sendPagePerformance();

  initBrowserLog();

  const api = apiClient({
    useMocks: window.CONFIG.ENV === 'local',
    zone,
  });

  const router = createBrowserRouter([
    {
      path: '/:language?/settings',
      element: <App />,
    },
    {
      path: '/:language?/settings/visibility',
      element: <App />,
    },
    {
      path: '/:language?/settings/notifications',
      element: <App />,
    },
    {
      path: '/:language?/settings/notifications/resubscribe',
      element: <App />,
    },
    {
      path: '/:language?/settings/notifications/unsubscribe',
      element: <App />,
    },
  ]);

  const store = createStore(country, brand as Brand, languageCode, zone, api);
  store.dispatch(
    updatePageBrandCountryZone(
      country,
      brand as Brand,
      languageCode,
      zone,
      site,
    ),
  );

  const initialTrackingState = {
    brand: brand as Brand,
    zone,
    siteCountry: country,
    siteLanguage: languageCode,
    userAgent: global?.navigator?.userAgent,
    actionOrigin: 'settings' as const,
    siteSection: 'myaccount' as const,
  };

  const analytics = createAnalytics(initialTrackingState);

  hydrateFeatures();

  const container = document.getElementById('app');

  hydrateRoot(
    container!,
    <Provider store={store}>
      <VocabProvider language={vocabLanguage}>
        <MelwaysProvider.Client site={site}>
          <ApolloProvider client={api.graphqlClient}>
            <ApiProvider zone={zone}>
              <AnalyticsProvider value={analytics}>
                <BraidProvider theme={seekJobs}>
                  <ToastProvider>
                    <RouterProvider router={router} />
                  </ToastProvider>
                </BraidProvider>
              </AnalyticsProvider>
            </ApiProvider>
          </ApolloProvider>
        </MelwaysProvider.Client>
      </VocabProvider>
    </Provider>,
  );
};
